import type { AxiosRequestConfig } from 'axios';

import axios from 'axios';

import { handleItemNotFoundRedirect } from 'src/utils/item-not-found-redirect';

import { getConfig } from 'src/global-config';

import { getAxiosToken, refreshHRToken  } from './axios-store';

/**
 * Axios Instance Configuration with Dynamic Token Handling
 * 
 * This axios instance uses a request interceptor to dynamically set the Authorization header
 * for each request, rather than setting it statically at initialization time.
 * 
 * The interceptor checks for tokens in the following order:
 * 1. customToken in localStorage (for custom authentication)
 * 2. jwt_access_token in sessionStorage (for JWT authentication)
 * 
 * This approach solves the issue where tokens were being read from storage before
 * they were set, resulting in empty Authorization headers.
 * 
 * The interceptor handles both JSON-stringified tokens (customToken) and plain string tokens (JWT).
 */

// ----------------------------------------------------------------------

export function getAxiosInstance() {
  const CONFIG = getConfig();

  const instance = axios.create({
    baseURL: CONFIG.develop.baseUrl,
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      const token = getAxiosToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      else{
        console.warn('HR Auth Token not found');
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => response,

    async (error) => {
      console.log('error', error.response);
      const originalRequest = error.config;

      // Network error / no response
      if (!error.response) {
        return Promise.reject(error);
      }

      if (handleItemNotFoundRedirect(error)) {
        return new Promise(() => {});
      }

      // Only handle 401
      if (error.response.status !== 401) {
        console.log('error.response.status', error.response.status);
        //here we have to check if status is 404 and 
        return Promise.reject(error);
      }

      // Prevent infinite retry loop
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        //refresh (single-flight)
        await refreshHRToken();

        //token from memory
        const newToken = getAxiosToken();

        if (!newToken) {
          // Refresh failed → logout already handled
          return Promise.reject(error);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return instance(originalRequest);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }
  );

  return instance;
}


// ----------------------------------------------------------------------

export const fetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const axiosInstance = getAxiosInstance();
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.get(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to fetch:', error);
    throw error;
  }
};

export const deleteFetcher = async (args: string | [string, AxiosRequestConfig]) => {
  try {
    const axiosInstance = getAxiosInstance();
    const [url, config] = Array.isArray(args) ? args : [args];

    const res = await axiosInstance.delete(url, { ...config });

    return res.data;
  } catch (error) {
    console.error('Failed to delete:', error);
    throw error;
  }
};

export const postFetcher = async ([url, body]: [string, Record<string, any> | string]) => {
  try {
    const axiosInstance = getAxiosInstance();
    const res = await axiosInstance.post(url, body);
    return res.data;
  } catch (error) {
    console.error('Failed to fetch (POST):', error);
    throw error;
  }
};

// export const multipleFetcher = async (args: string | [string, AxiosRequestConfig][]) => {
//   const res = await Promise.all(args.map(fetcher));
//   return res;
// };

// ----------------------------------------------------------------------
export const updateCall = async (url: string, data: any, config: AxiosRequestConfig, method: 'POST' | 'PUT' = 'PUT') => {
  try {
    const axiosInstance = getAxiosInstance();
    const res = method === 'POST' 
      ? await axiosInstance.post(url, data, { ...config })
      : await axiosInstance.put(url, data, { ...config });
    return res.data;
  } catch (error) {
    console.error(`Failed to ${method.toLowerCase()}:`, error);
    throw error;
  }
};

// ----------------------------------------------------------------------

/**
 * Helper function to get the authentication token from storage
 * Uses the same logic as the axios interceptor for consistency
 */
function getAuthToken(): string | null {
  const token = localStorage.getItem('customToken');
  if (token) {
    try {
      let parsedToken = token;
      if (token.startsWith('"') || token.startsWith('{')) {
        const parsed = JSON.parse(token);
        parsedToken = typeof parsed === 'string' ? parsed : token;
      }
      if (parsedToken && typeof parsedToken === 'string') {
        return parsedToken;
      }
    } catch (error) {
      console.warn('Failed to parse token from storage:', error);
    }
  }
  return null;
}

/**
 * Generic streaming function for Server-Sent Events or streaming responses
 * Automatically handles authentication using the same token logic as axios
 * 
 * @param endpoint - The API endpoint to call (e.g., endpoints.knowledge_base.chatStream)
 * @param data - The data to send in the POST request body
 * @param onStreamChunk - Callback function to handle each chunk of streamed data
 * @param config - Optional additional fetch configuration
 */
// Server JSON-encodes each stream chunk so newlines/whitespace survive SSE framing intact.
// Decode back to the original text; fall back to the raw payload for any non-JSON-string value
// (e.g. an older backend that streamed plain text).
const emitStreamChunk = (payload: string, onStreamChunk: (chunk: string) => void) => {
  let text = payload;
  try {
    const parsed = JSON.parse(payload);
    if (typeof parsed === 'string') text = parsed;
  } catch {
    // keep raw payload
  }
  onStreamChunk(text);
};

export const streamFetcher = async (
  endpoint: string,
  data: any,
  onStreamChunk: (chunk: string) => void,
  config?: RequestInit
) => {
  const CONFIG = getConfig();
  const url = `${CONFIG.develop.baseUrl}${endpoint}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "Accept": "text/event-stream",
  };

  if (config?.headers) {
    const configHeaders = new Headers(config.headers);
    configHeaders.forEach((value, key) => (headers[key] = value));
  }

  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  try {
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(data),
      headers,
      ...config,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    if (!response.body) {
      throw new Error("Readable stream not supported.");
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // FIX #2: Split by SSE event delimiter
      const events = buffer.split("\n\n");
      buffer = events.pop() || ""; // part of next event

      for (const event of events) {
        const line = event.trim();
        if (!line || !line.startsWith("data:")) continue;

        // Per SSE spec: strip only the single optional space after "data:"
        const raw = line.substring(5);
        const payload = raw.startsWith(' ') ? raw.substring(1) : raw;

        // FIX #3: Stop on [DONE]
        if (payload === "[DONE]") return;

        emitStreamChunk(payload, onStreamChunk);
      }
    }

    // Handle leftover buffer (unlikely but safe)
    const last = buffer.trim();
    if (last.startsWith("data:")) {
      const rawLast = last.substring(5);
      const payload = rawLast.startsWith(' ') ? rawLast.substring(1) : rawLast;
      if (payload !== "[DONE]") emitStreamChunk(payload, onStreamChunk);
    }
  } catch (error) {
    console.error("Streaming error:", error);
    throw error;
  }
};


// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/auth/me',
    signIn: '/api/auth/sign-in',
    signUp: '/api/auth/sign-up',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  auth0: {
    exchangeToken: '/api/Account/Token',
    verifyEmail: '/api/Account/RequestEmailConfirmation',
    onBoarding: '/api/Account/Onboarding',
  },
  account: {
    profile: '/api/Account/Users/Profile',
    resetPassword: '/api/Account/Users/ResetPassword',
    organisation: '/api/Organizations',
    profilePicture: '/api/Account/Users/ProfilePicture'
  },
  jobs: {
    list: '/api/jobPostings',
    create: '/api/JobPostings',
    details: (jobId: string) => `/api/JobPostings/${jobId}/Details`,
    workflowStages: (jobId: string) => `/api/JobPostings/${jobId}/WorkflowStages`,
    stageCounts: (jobId: string) => `/api/Candidates/${jobId}/StageCounts`,
  },
  members: {
    list: '/api/Account/Users',
  },
  invitations: {
    send: '/api/Account/Users/Invitations',
    get: '/api/Account/Users/Invitations',
    delete: (id: string) => `/api/Account/Users/Invitations/${id}`,
    reInvite: (id: string) => `/api/Account/Users/Invitations/${id}/Resend`,
    validate: (id: string) => `/api/Account/Users/Invitations/${id}/PreValidate`,
    acceptInvitation: (id: string) => `/api/Account/Users/Invitations/${id}/Complete`
  },
  candidate_risk: {
    get: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/DetailedRisk`,
    update: (id: number) => `api/Candidates/${id}/RiskItem`,
    runRiskSummary: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/evaluate-tool`,
  },
  create_job: {
    aiEnhancement: '/api/JobPostings/ProcessContent',
    getChecklist: '/api/JobPostings/JobChecklists',
    parseJobDescription: '/api/JobPostings/ParseJobChecklist',
    fixJobDescription: '/api/JobPostings/FixJobDescriptionWithChecklist',
    getEvaluationCriteria: '/api/JobPostings/ParseJobPostingCriteria'
  },
  candidates: {
    getAdvanceFilter: '/api/Candidates/AdvanceFilter',
    getOptions: (jobId: string|number) => `/api/Candidates/${jobId}/AdvancedFilter/SelectionData`,
    query: '/api/Candidates/QueryCandidates',
    compare: (jobId: string, candidateIds: number[]) => {
      const params = new URLSearchParams();
      candidateIds.forEach((id) => params.append('candidateIds', String(id)));
      return `/api/Candidates/${jobId}/Compare?${params.toString()}`;
    },
    details: (jobId: string, candidateId: string | number) =>
      `/api/Candidates/${jobId}/${candidateId}/Details`,
  },
  candidate_scorecard: {
    get: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/DetailedScoreCard`,
  },

  /**
 * Endpoint map for workflow plan creation and update.
 *
 * First-level key:
 * - true  => stage is in draft
 * - false => stage is published
 *
 * Second-level key:
 * - true  => plan is in draft
 * - false => plan is published OR plan does not exist
 *
 * Why `false` can mean two things for plan:
 * - If `Plan` is null, then there is no plan yet, so we use POST to create one.
 * - If `Plan` exists and `IsPlanDraft` is false, then the plan is published,
 *   so we use PUT to create/edit its draft version.
 *
 * Endpoint usage summary:
 *
 * 1. Stage draft + no plan
 *    - use: job_workflows.true.false.post
 *    - purpose: create a draft plan under a draft stage
 *
 * 2. Stage draft + draft plan
 *    - use: job_workflows.true.true.put
 *    - purpose: update existing draft plan under a draft stage
 *
 * 3. Stage published + no plan
 *    - use: job_workflows.false.false.post
 *    - purpose: create a draft stage/plan flow for a published stage
 *
 * 4. Stage published + draft plan
 *    - use: job_workflows.false.false.put
 *    - purpose: update an already existing draft plan
 *
 * 5. Stage published + published plan
 *    - use: job_workflows.false.true.put
 *    - purpose: create/edit the draft version of a published plan
 */

  job_workflows: {
    get: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/editor`,
    update: (jobId: string) => `/api/JobPostings/${jobId}/WorkflowStages`,
    delete: (jobId: string, stageId: string) => `/api/JobPostings/${jobId}/WorkflowStages/${stageId}`,
      true: {  // published stage
        true: {  // has published plan
          put: (jobId: string, publishedStageId: string, publishedPlanId: string) =>
            `/api/WorkflowDraft/jobPostings/${jobId}/workflow/stages/${publishedStageId}/plan/${publishedPlanId}/draft`,
        },
        false: {  // no published plan (new or already-drafted)
          // call put when DraftPlanId exists, post when DraftPlanId is null
          put: (jobId: string, draftPlanId: string) =>
            `/api/WorkflowDraft/jobPostings/${jobId}/workflow/plan/draft/${draftPlanId}`,
          post: (jobId: string, publishedStageId: string) =>
            `/api/WorkflowDraft/jobPostings/${jobId}/workflow/stages/${publishedStageId}/plan/draft`,
        },
      },
      false: {  // draft-only stage (IsNewStage = true)
        false: {
          // call put when DraftPlanId exists, post (upsert) when DraftPlanId is null
          put: (jobId: string, draftPlanId: string) =>
            `/api/WorkflowDraft/jobPostings/${jobId}/workflow/plan/draft/${draftPlanId}`,
          post: (jobId: string, draftStageId: string) =>
            `/api/WorkflowDraft/jobPostings/${jobId}/workflow/stages/draft/${draftStageId}/plan`,
        },
      },
    create_stage: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/stages/draft`,
    delete_stage: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/stages/draft`,
    delete_plan: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/plan/draft`,
    publish: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/publish`,
    discard: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/discard`,
    reorder: (jobId: string) => `/api/WorkflowDraft/jobPostings/${jobId}/workflow/stages/draft/reorder`,
  },
  candidate_comment: {
    get: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/Comments`,
    create: 'api/Candidates/Comments',
    update: (commentId: string) => `api/Candidates/Comments/${commentId}`,
    delete: (commentId: string) => `api/Candidates/Comments/${commentId}`,
    bulkCreate: 'api/Candidates/Comments/Bulk',
  },
  unprocessed_candidates: {
    get: (jobId: string, status: string) => `/api/Candidates/${jobId}/ByEvaluationStatus/${status}`,
  },
  candidate_interview: {
    send: `api/Interview/SendEncodedLink`,
  },
  interview_hub: {
    get: (jobId: string) => `/api/Interview/${jobId}`,
    extendLink: (interviewLinkId: string) => `/api/Interview/${interviewLinkId}/UpdateExpiry`,
    cancelLink: (interviewLinkId: string) => `/api/Interview/${interviewLinkId}/Cancel`,
  },
  interview_agenda: {
    get: (jobId: string) => `/api/JobPostings/${jobId}/InterviewAgenda`,
    update: (interviewAgendaId: string) => `/api/JobPostings/InterviewAgenda/${interviewAgendaId}`,
    add: '/api/JobPostings/InterviewAgenda',
  },
  knowledge_base: {
    upload: '/api/OrganizationRag/UploadDocument',
    uploadLink: '/api/OrganizationRag/AddLink',
    uploadQA: 'api/OrganizationRag/AddQA',
    deleteKnowledgeItem: (id: string) => `api/OrganizationRag/sources/${id}`,
    sourceAutoSync: (id: string | number) => `api/OrganizationRag/sources/${id}/auto-sync`,
    sourceRescan: (id: string | number) => `api/OrganizationRag/sources/${id}/rescan`,
    sourceReupload: (id: string | number) => `api/OrganizationRag/sources/${id}/reupload`,
    getKnowledgeItems: 'api/OrganizationRag/sources',
    chatStream: '/api/OrganizationRag/Query/Stream',
    essentials: 'api/OrganizationRag/essentials',
    essentialsCatalog: 'api/OrganizationRag/essentials/catalog',
    essentialAnswer: (key: string) => `api/OrganizationRag/essentials/${key}/answer`,
    essentialApprove: (key: string) => `api/OrganizationRag/essentials/${key}/approve`,
  },
  interviewer_persona: {
    get: (organizationId: string) => `/api/Organizations/${organizationId}/VoiceAgent`,
    update: (organizationId: string) => `/api/Organizations/${organizationId}/VoiceAgent`,
    add: '/api/Organizations/VoiceAgent',
  },
  candidate_evaluation: {
    get: (jobId: string, candidateId: string) => `/api/Interview/${jobId}/${candidateId}/Analysis`,
    get_detailed_evaluation: (interviewId: string) => `/api/Interview/${interviewId}/Details`,
  },
  email_template: {
    upload: '/api/EmailTemplates',
    list: '/api/EmailTemplates',
    update: (templateId: string) => `/api/EmailTemplates/${templateId}`,
    delete: (templateId: string) => `/api/EmailTemplates/${templateId}`,
  },
  email_compose: {
    send_batch: '/api/EmailMessages/SendBatch',
    get_emails: '/api/EmailMessages/History/Applicants'
  },
  billing: {
    subscribe: '/api/billing/subscribe',
    portalSession: '/api/billing/portal-session',
    cancel: '/api/billing/cancel',
    reactivate: '/api/billing/reactivate',
    // Org is resolved from the JWT — no org ID in the URL
    orgStatus: '/api/billing/org-status',
    products: '/api/billing/products',
    invoices: '/api/billing/invoices',
    paymentMethod: '/api/billing/payment-method',
    updatePaymentMethod: '/api/billing/update-payment-method',
    interviewOptions: '/api/billing/interview-options',
    grantEventsTrial: '/api/billing/grant-events-trial',
    purchaseCredits: '/api/billing/purchase-credits',
  },
  interview_evaluation: {
    get: (jobId: string) => `/api/JobPostings/${jobId}/criteria`,
    update: (evaluationId: string, jobId: string) => `/api/JobPostings/criteria/${evaluationId}`,
    delete: (evaluationId: string, jobId: string) => `/api/JobPostings/criteria/${evaluationId}`,
    add: (jobId: string) => `/api/JobPostings/${jobId}/criteria`,
  },
  candidate_evaluation_scorecard: {
    get_round_details: (jobId: string, candidateId: string) => `/api/interview/${jobId}/${candidateId}/Rounds`,
    get_round_scorecard: (roundId: string) => `/api/interview/${roundId}/DetailedScoreCard `,
    get_overall_scorecard: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/DetailedScoreCard`,
    get_interview_rounds: (jobId: string) => `/api/interviewplans/jobPostings/${jobId}/summaries`,
    get_human_rounds: (jobId: string) => `/api/interview/${jobId}/HumanRounds`,
    save_human_scorecard: (jobId: string, candidateId: string) => `/api/Interview/${jobId}/${candidateId}/ManualScore`,
    get_resume_round: (jobId: string, candidateId: string) => `/api/interview/${jobId}/${candidateId}/ResumeScoreCard`,
    get_round_overview: (jobId: string, candidateId: string, roundId: string) => `/api/candidates/${jobId}/${candidateId}/RoundDetail/${roundId}`,
  },
  assessment_submission: {
    get: (interviewId: string | number) => `/api/Interview/${interviewId}/AssessmentSubmission`,
    download: (interviewId: string | number) => `/api/Interview/${interviewId}/AssessmentSubmission/Download`,
    promptingAnalysis: (interviewId: string | number) =>
      `/api/Interview/${interviewId}/AssessmentSubmission/PromptingAnalysis`,
  },
  candidate_evaluation_overview: {
    get_overview: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/AggregatedScorecard`,
  },
  candidate_overview: {
    get: (jobId: string, candidateId: string) => `/api/Candidates/${jobId}/${candidateId}/Overview`,
  },
};
